import { Controller } from "@hotwired/stimulus";
import { createRoot } from "react-dom/client";
import { useEffect, useState, useRef } from "react";
import { marked } from "marked";

import Drawer from "./drawer";
import TextArea from "./textArea";

marked.setOptions({
  headerIds: false, // Disable automatic header IDs
  mangle: false, // Disable mangling
  breaks: true, // Enable line breaks
  gfm: true, // Enable GitHub Flavored Markdown
});

function Component({
  minRows = 4,
  rows = 4,
  value = "",
  className,
  name,
  showMenubar,
  placeholder,
  autofocus = false,
}: {
  minRows: number;
  rows: number;
  value: string;
  className: string;
  name: string;
  showMenubar?: true;
  placeholder?: string;
  autofocus?: boolean;
}) {
  const [opened, setOpened] = useState(false);
  const [currentText, setCurrentText] = useState<string>(value);
  const inputTextareaRef = useRef(null);

  return (
    <>
      <TextArea
        ref={inputTextareaRef}
        onChange={(text) => setCurrentText(text)}
        className={className}
        name={name}
        minRows={minRows}
        rows={rows}
        value={currentText}
        openAiDrawer={() => setOpened(true)}
        showMenubar={showMenubar}
        placeholder={placeholder}
        autofocus={autofocus}
      />
      {opened && (
        <Drawer
          className={className}
          name={name}
          onClose={() => setOpened(false)}
          onSelectAIText={(markdown) => {
            const html = marked(markdown);
            setCurrentText(html);
            setOpened(false);
          }}
        />
      )}
    </>
  );
}

export default class extends Controller {
  static targets = ["ai"];

  connect() {
    this.root = createRoot(this.element);
    const textarea = this.element.querySelector("textarea");
    const className = this.element.dataset.classname;
    const value = this.element.dataset.value;
    const name = this.element.dataset.name;
    const minRows = this.element.dataset.minrows || 4;
    const rows = this.element.dataset.rows || minRows;
    const showMenubar = !!Number(this.element.dataset.showMenubar);
    const placeholder = this.element.dataset.placeholder;
    const autofocus = this.element.dataset.autofocus === "true";
    this.root.render(
      <Component
        className={className}
        value={value}
        name={name}
        minRows={Number(minRows)}
        rows={Number(rows)}
        showMenubar={showMenubar}
        placeholder={placeholder}
        autofocus={autofocus}
      />,
    );
  }

  disconnect() {
    if (this.root) {
      this.root.unmount();
    }
  }
}
